import { LegalEntityMatchResult } from '../../../models/bill-import.models';
import { MeterEditData } from '../state/bill-ai-flow.models';
import {
  FieldError,
  StepValidationResult,
  ValidationRule,
  getByPath,
  isEmpty,
} from '../validation';

/**
 * Rules for the meter step — applied to every meter regardless of new/matched.
 * Even for existing meters the utility/commodity/UAN fields drive pricing,
 * rate checks, and supplier matching downstream, so they cannot be blank.
 */
export const METER_RULES: ValidationRule[] = [
  { field: 'utilityAccountNumber1', label: 'Utility Account Number', severity: 'required' },
  { field: 'commodityType',         label: 'Commodity Type',         severity: 'required' },
  { field: 'utilityId',             label: 'Utility',                severity: 'required' },
  { field: 'rateClassId',           label: 'Rate Class',             severity: 'warn' },
];

/**
 * Synthetic field key for the cross-utility error — surfaced as an inline
 * banner under the offending meter. A single physical address cannot be
 * served by two different utilities for the same commodity, so when the
 * user assigns sibling meters under one SA to different utility IDs, every
 * meter in that conflict is flagged.
 */
export const METER_CROSS_UTILITY_FIELD = '_crossUtility';

export interface MeterValidationInput {
  legalEntities: LegalEntityMatchResult[];
  meterEdits: Record<string, MeterEditData>;
  isNewEntity: (groupId: string) => boolean;
}

/** Build a human-readable label for a meter error */
function describeMeter(meterGroupId: string, edit: MeterEditData | undefined): string {
  const uan = edit?.utilityAccountNumber1?.trim();
  if (uan) return `Meter ${uan}`;
  return `Meter ${meterGroupId.slice(0, 8)}`;
}

export function validateMeterStep(input: MeterValidationInput): StepValidationResult {
  const errors: FieldError[] = [];
  const warnings: FieldError[] = [];

  for (const len of input.legalEntities) {
    for (const sa of len.serviceAddresses) {
      for (const meter of sa.meters) {
        const edit = input.meterEdits[meter.groupId] ?? {};
        const entityLabel = describeMeter(meter.groupId, edit);
        const isNew = input.isNewEntity(meter.groupId);

        for (const rule of METER_RULES) {
          if (rule.condition && !rule.condition({ entity: edit, isNew })) {
            continue;
          }
          const value = getByPath(edit, rule.field);
          if (isEmpty(value)) {
            const err: FieldError = {
              entityId: meter.groupId,
              entityLabel,
              field: rule.field,
              label: rule.label,
              severity: rule.severity,
            };
            if (rule.severity === 'required') errors.push(err);
            else warnings.push(err);
          }
        }
      }

      // Cross-utility check, scoped per SA: group sibling meters by
      // commodity, flag every meter whose commodity has 2+ distinct
      // utility IDs. Each such meter gets its own error row so the inline
      // banner can render under the offending row(s).
      // Skip meters whose commodityType is unknown — bucketing them under a
      // sentinel produced both false positives (two undefined-commodity
      // meters with different utilities flagged) and false negatives
      // (Electricity vs undefined sibling never compared).
      const utilByCommodity = new Map<string, Set<number>>();
      for (const meter of sa.meters) {
        const mEdit = input.meterEdits[meter.groupId];
        const utilityId = mEdit?.utilityId ?? null;
        const commodity = mEdit?.commodityType;
        if (utilityId == null || !commodity) continue;
        const set = utilByCommodity.get(commodity) ?? new Set<number>();
        set.add(utilityId);
        utilByCommodity.set(commodity, set);
      }
      const conflictingCommodities = new Set<string>();
      for (const [commodity, utilityIds] of utilByCommodity) {
        if (utilityIds.size > 1) conflictingCommodities.add(commodity);
      }
      if (conflictingCommodities.size > 0) {
        for (const meter of sa.meters) {
          const mEdit = input.meterEdits[meter.groupId];
          const commodity = mEdit?.commodityType;
          if (!commodity || !conflictingCommodities.has(commodity)) continue;
          if (mEdit?.utilityId == null) continue;
          errors.push({
            entityId: meter.groupId,
            entityLabel: describeMeter(meter.groupId, mEdit),
            field: METER_CROSS_UTILITY_FIELD,
            label: 'Utility',
            severity: 'required',
            summaryMessage: 'Conflicts with another meter’s utility at this address',
          });
        }
      }
    }
  }

  return { errors, warnings, hasBlockers: errors.length > 0 };
}
